import React, { useEffect, useState, useCallback } from 'react'
import { useRouter } from '@/react/hooks'
import {
  CommonPageHeader,
  PageSwitcher,
  Search,
  useAngularServices,
} from '@/react/components'
import { css } from '@emotion/react'
import { ConfigNavBar } from '../AddProject/components/ConfigNavBar'
import { DefaultUserIcon } from '@/react/componentAssets/DefaultUserIcon'
import { ThinCheckmark } from '@/react/componentAssets/ThinCheckmark'
import classNames from 'classnames'
import 'react-tooltip/dist/react-tooltip.css'
import sortArrows from '@/assets/icons/sort-arrows.svg'
import sortActiveArrows from '@/assets/icons/sort-arrows-active.svg'
import { hexToRGBA } from '@/utils/generalUtilities.js'
import sortInactiveArrows from '@/assets/icons/sort-arrows-inactive.svg'
import { TableHighlightWrapper } from './components/TableHighlightWrapper'
import { EllipsisWithTooltipOnly } from './components/EllipsisWithTooltipOnly'

interface User {
  id: number
  first_name: string
  last_name: string
  username: string
  has_project?: boolean
  photo_url?: string
  employee_id?: string
  role?: string
  projects: number[]
}

interface Project {
  id: number
  name: string
  users: number[]
}

interface UsersResponse {
  results: User[]
  count: number
}

export const ProjectUserForm = () => {
  const { stateService } = useRouter()
  const { Api, CurrentUser } = useAngularServices()
  const variableColor = CurrentUser.getClientSettings().web_primary_color
  const projectTitle = CurrentUser.getClientSettings().project_language
  const projectID = Number(stateService.params.projectID)
  const userRole = CurrentUser.getRole()
  const [project, setProject] = useState<Project | null>(null)
  const [projectName, setProjectName] = useState<string | null>(null)
  const [users, setUsers] = useState<User[]>([])
  const [totalUsers, setTotalUsers] = useState(0)
  const [page, setPage] = useState(1)
  const [search, setSearch] = useState('')
  const [order, setOrder] = useState('first_name')
  const [reverse, setReverse] = useState(false)
  const [selectAll, setSelectAll] = useState(false)
  const [lastSortedField, setLastSortedField] = useState<string | null>(null)
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)

  useEffect(() => {
    if (userRole !== 'client_admin' && userRole !== 'supervisor') {
      stateService.go('admin.projects.list')
    }
  }, [])
  const ROLE_LABELS: Record<string, string> = {
    client_admin: 'Administrator',
    supervisor: 'Supervisor',
    client_user: 'Employee',
    read_only: 'Field Employee',
    trainee: 'Trainee',
    trainee_manager: 'Training Manager',
    recipient: 'Recipient',
  }

  const formatRole = (role: string) => ROLE_LABELS[role] || role
  const fetchData = useCallback(() => {
    if (!projectID) return

    const query = {
      page: order === 'has_project' ? 1 : page,
      page_size: order === 'has_project' ? 1000 : 20,
      search,
      order: reverse ? `-${order}` : order,
      project_id: projectID,
      is_active: true,
    }

    Api.Projects.byID(projectID, (proj: Project) => {
      setProject(proj)
      setProjectName(proj.name)
    })

    Api.Users.get(query, (response: UsersResponse) => {
      let allUsers = response.results.map((u) => ({
        ...u,
        projects: Array.isArray(u.projects) ? u.projects : [],
      }))

      if (order === 'has_project') {
        allUsers = allUsers.sort((a: User, b: User) => {
          const aVal = a.projects.includes(projectID) ? 1 : 0
          const bVal = b.projects.includes(projectID) ? 1 : 0
          return reverse ? aVal - bVal : bVal - aVal
        })

        setSelectAll(
          allUsers.every((u: User) => u.projects.includes(projectID)),
        )

        const pageSize = 20
        const startIndex = (page - 1) * pageSize
        const endIndex = startIndex + pageSize
        const paginated = allUsers.slice(startIndex, endIndex)

        setUsers(paginated)
        setTotalUsers(allUsers.length)
      } else {
        setUsers(allUsers)
        setTotalUsers(response.count)
      }

      setSelectAll(allUsers.every((u: any) => u.projects.includes(projectID)))
    })
  }, [page, search, order, reverse, projectID])

  useEffect(() => {
    fetchData()
  }, [page, search, order, reverse, fetchData])

  const toggleUser = async (user: User) => {
    if (!project) return

    const isAssigned = project.users.includes(user.id)
    const updatedUsers = isAssigned
      ? project.users.filter((uid: number) => uid !== user.id)
      : [...project.users, user.id]

    await Api.Projects.patch({ id: project.id, users: updatedUsers })
    fetchData()
  }

  const handleToggleAllUsers = async (checked: boolean) => {
    setSelectAll(checked)

    const userIds = users.map((u) => u.id)
    const updatedUserIds = checked
      ? [...new Set([...project.users, ...userIds])]
      : project.users.filter((uid: number) => !userIds.includes(uid))

    await Api.Projects.patch({
      id: projectID,
      users: updatedUserIds,
    })
    fetchData()
  }

  const handleSort = (field: string) => {
    if (field === lastSortedField) {
      setReverse((prev) => !prev)
    } else {
      const fieldInitialReverseMap: Record<string, boolean> = {
        has_project: false,
        employee_id: true,
        first_name: false,
        username: false,
        role: false,
      }

      setReverse(fieldInitialReverseMap[field] ?? false)
      setLastSortedField(field)
    }
    setOrder(field)
  }

  const handleDoneClick = () => {
    stateService.go(
      'admin.projects.list',
      {
        pageNumber: stateService.params.pageNumber,
        search: stateService.params.search,
        order: stateService.params.order,
        reverse: stateService.params.reverse,
      },
      { reload: false },
    )
  }

  return (
    <section css={baseStyle(variableColor)}>
      <CommonPageHeader headerText={` ${projectTitle}`} />
      <ConfigNavBar />

      <div className="wrapper wrapper-content animated fadeInRight">
        <div className="row">
          <div className="col-lg-12">
            <div className="ibox float-e-margins">
              <div className="ibox-title d-flex align-items-center">
                <div className="ibox-title-block">
                  <EllipsisWithTooltipOnly
                    text={`Assign to Project: ${projectName}`}
                    width={'99%'}
                  />
                </div>
                <div className="remove-float ml-auto">
                  <button
                    className="btn btn-default-general btn-default-primary"
                    onClick={handleDoneClick}
                  >
                    Done
                  </button>
                </div>
              </div>

              <div className="ibox-content">
                <div className="row d-flex align-items-center">
                  <div className="search__block">
                    <Search
                      search={search}
                      searchCallback={setSearch}
                      pageCallback={setPage}
                      updateUrl={false}
                      width={264}
                    />
                  </div>
                </div>

                <div className="table-responsive">
                  <table className="table table-striped">
                    <thead>
                      <tr>
                        <th className="col-headshot">Headshot</th>
                        <th
                          className="col-user"
                          onClick={() => handleSort('first_name')}
                        >
                          <div>
                            <span>User</span>
                          </div>
                        </th>
                        <th
                          className="col-id"
                          onClick={() => handleSort('employee_id')}
                        >
                          <div>
                            <span>ID</span>
                          </div>
                        </th>
                        <th
                          className="col-username"
                          style={{ cursor: 'pointer' }}
                          onClick={() => handleSort('username')}
                        >
                          Username
                        </th>
                        <th
                          className="col-role"
                          style={{ cursor: 'pointer' }}
                          onClick={() => handleSort('role')}
                        >
                          Role
                        </th>
                        <th className="col-checkbox">
                          <div
                            style={{
                              height: '30px',
                              width: '30px',
                              display: 'flex',
                              cursor: 'pointer',
                            }}
                          >
                            <label className="checkbox-container">
                              <input
                                type="checkbox"
                                checked={selectAll}
                                onChange={(e) =>
                                  handleToggleAllUsers(e.target.checked)
                                }
                              />
                              <span
                                className={classNames('checkmark', {
                                  active: selectAll,
                                })}
                              >
                                {selectAll && (
                                  <ThinCheckmark color={variableColor} />
                                )}
                              </span>
                            </label>
                            <img
                              src={
                                order !== 'has_project'
                                  ? sortArrows
                                  : reverse
                                  ? sortInactiveArrows
                                  : sortActiveArrows
                              }
                              onClick={() => handleSort('has_project')}
                              alt="Sort"
                              width={16}
                              height={16}
                              style={{
                                marginLeft: '10px',
                                marginBottom: '3px',
                              }}
                            />
                          </div>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {users.map((user) => {
                        const isChecked =
                          Array.isArray(user.projects) &&
                          user.projects.includes(projectID)
                        return (
                          <tr key={user.id}>
                            <td className="col-headshot">
                              <div style={{ justifyContent: 'center' }}>
                                {user.photo_url ? (
                                  <img
                                    src={user.photo_url}
                                    style={{
                                      width: 30,
                                      height: 30,
                                      borderRadius: '0%',
                                      objectFit: 'cover',
                                      cursor: 'pointer',
                                    }}
                                    onClick={() =>
                                      setPreviewUrl(user.photo_url!)
                                    }
                                  />
                                ) : (
                                  <DefaultUserIcon height={30} width={30} />
                                )}
                              </div>
                            </td>

                            <td className="col-user">
                              <div>
                                <TableHighlightWrapper
                                  variableColor={hexToRGBA(variableColor)}
                                  text={`${user.first_name} ${user.last_name}`}
                                  search={search}
                                  width={136}
                                />
                              </div>
                            </td>
                            <td className="col-id">
                              <div>
                                <TableHighlightWrapper
                                  variableColor={hexToRGBA(variableColor)}
                                  text={user.employee_id || ''}
                                  search={search}
                                  width={82}
                                />
                              </div>
                            </td>
                            <td className="col-username">
                              <div>
                                <TableHighlightWrapper
                                  variableColor={hexToRGBA(variableColor)}
                                  text={user.username}
                                  search={search}
                                  width={240}
                                />
                              </div>
                            </td>
                            <td className="col-role">
                              <div>
                                <TableHighlightWrapper
                                  variableColor={hexToRGBA(variableColor)}
                                  text={user.role ? formatRole(user.role) : ''}
                                  search={search}
                                  width={165}
                                />
                              </div>
                            </td>
                            <td className="checkmark__td">
                              <div
                                style={{
                                  height: '30px',
                                  width: '30px',
                                  display: 'flex',
                                }}
                              >
                                <label className="checkbox-container">
                                  <input
                                    type="checkbox"
                                    checked={isChecked}
                                    onChange={() => void toggleUser(user)}
                                  />
                                  <span
                                    className={classNames('checkmark', {
                                      active: isChecked,
                                    })}
                                  >
                                    {isChecked && (
                                      <ThinCheckmark color={variableColor} />
                                    )}
                                  </span>
                                </label>
                              </div>
                            </td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                  <div className="page-switcher-base">
                    <PageSwitcher
                      pageLength={20}
                      listCount={totalUsers}
                      currentPage={page}
                      callback={setPage}
                      updateUrl={false}
                    />
                  </div>
                  {previewUrl && (
                    <div
                      className="preview-backdrop"
                      onClick={() => setPreviewUrl(null)}
                    >
                      <img
                        src={previewUrl}
                        alt="Preview"
                        className="preview-image"
                        onClick={(e) => e.stopPropagation()}
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

const baseStyle = (variableColor: string) =>
  // @ts-ignore
  css({
    paddingBottom: 'calc(5rem + 100px)',

    '.wrapper-content': {
      paddingTop: 0,
      maxWidth: '831px',
      paddingLeft: '11px',
    },
    '.ibox-title': {
      border: 0,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: 0,
      paddingTop: '16px',
      paddingLeft: 20,
      paddingRight: 20,

      '.ibox-title-block': {
        color: ' #575757',
        fontFamily: 'Open Sans',
        fontSize: '13px',
        fontStyle: 'normal',
        fontWeight: 600,
        lineHeight: 'normal',
        letterSpacing: '0.13px',

        whiteSpace: 'normal',
        wordBreak: 'break-word',
        overflowWrap: 'break-word',
        maxWidth: '100%',
        minWidth: 'calc(100% - 10rem)',
        display: '-webkit-box',
        WebkitLineClamp: 2,
        WebkitBoxOrient: 'vertical',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
      },
    },
    '.preview-backdrop': {
      position: 'fixed',
      top: 0,
      left: 0,
      width: '100vw',
      height: '100vh',
      backgroundColor: 'rgba(0,0,0,0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 9999,
    },
    '.preview-image': {
      maxWidth: '90vw',
      maxHeight: '90vh',
      backgroundColor: '#fff',
      padding: '8px',
      borderRadius: '4px',
      boxShadow: '0 0 10px rgba(0,0,0,0.3)',
    },

    '@media(max-width: 1150px)': {
      '.table-responsive': { marginTop: '25px !important' },
    },

    '.table-responsive': {
      marginTop: '-9px',
    },
    '.remove-float': {
      marginLeft: 'auto',
    },
    '.ibox-content': {
      padding: '19px 20px 1px 20px',
    },
    '.search__block': {
      marginLeft: 15,
    },
    '.btn-default-primary': {
      marginLeft: '10px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: 80,
      height: 28,
      backgroundColor: variableColor,
      color: '#fff',
    },
    table: {
      width: '100%',
      borderCollapse: 'collapse',
      tableLayout: 'fixed',
      tr: {
        maxHeight: 21,
      },
      tbody: {
        'tr:nth-of-type(odd)': {
          backgroundColor: '#f1f3f5',
        },
      },
      th: {
        verticalAlign: 'bottom !important',
        padding: '5px !important',
        height: 21,
        fontWeight: 700,
        fontSize: 13,
        textAlign: 'left',
        color: '#68696D',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        paddingBottom: '5px !important',

        div: {
          cursor: 'pointer',
          display: 'flex',
          height: '30px',
          flexDirection: 'row',
          alignItems: 'flex-end',
        },
      },
      td: {
        padding: '5px !important',
        textAlign: 'left',
        verticalAlign: 'middle',
        display: 'table-cell',
        fontSize: 13,
        height: '40px',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',

        div: {
          lineHeight: '28px',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          height: '30px',
          flexDirection: 'row',
          alignItems: 'center',
          display: 'block',
          width: '100%',
        },
      },
      '.checkbox-container': {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
        width: 22,
        height: 22,
        cursor: 'pointer',
        padding: 0,
        margin: 0,
      },

      '.checkbox-container input': {
        position: 'absolute',
        opacity: 0,
        width: '100%',
        height: '100%',
        margin: 0,
        zIndex: 2,
        cursor: 'pointer',
        appearance: 'none',
        WebkitAppearance: 'none',
        MozAppearance: 'none',
      },

      '.checkmark': {
        width: 22,
        height: 22,
        border: '1px solid #B3B3B3',
        backgroundColor: '#fff',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1,
        transition: 'background-color 0.2s, border-color 0.2s',
      },

      '.checkmark.active': {
        backgroundColor: 'white !important',
        borderColor: variableColor,
      },

      '.checkbox-container .checkmark::after': {
        display: 'none !important',
        content: '""',
      },

      'th.col-headshot, td.col-headshot': {
        width: 85,
        textAlign: 'center !important',
      },
      'th.col-user, td.col-user': {
        width: 136,
      },
      'th.col-id, td.col-id': {
        width: 82,
      },
      'th.col-username, td.col-username': {
        width: 240,
      },
      'th.col-role, td.col-role': {
        width: 165,
      },
      'th.col-checkbox, td.col-checkbox': {
        width: '46px',
        textAlign: 'center',
      },
    },
  })
